import React, { useContext, useEffect, useState, useCallback, useRef } from "react";
import { FilterContext } from "../../context/Filter";
import { axInstBasic } from "../../components/Service/Service";
import DealerTable from "../../components/Table/VehicleTable3"; // Assuming same table component
import SpareSalesDropDownTable from "../../components/Table/SpareSalesDropDownTable";


export const getArnPayload = (parArn, childArn, filterPayload = {}) => {
  const base = {
    ...(childArn ? { childArn } : { parArn }),
    ...filterPayload,
  };

  Object.keys(base).forEach((key) => {
    if (Array.isArray(base[key]) && base[key].length === 0) {
      delete base[key];
    }
  });

  return base;
};

// API Call 1 - Enhanced with abort signal
export const dropDownOptionsSparesSales = async (
  parArn,
  childArn,
  filterPayload,
  selectedReg,
  abortSignal
) => {
  if (!selectedReg) return [];

  const values = getArnPayload(parArn, childArn, filterPayload);
  const payload = {
    ...values,
    search_type: selectedReg,
  };

  try {
    const res = await axInstBasic.post(
      `${process.env.REACT_APP_API_URL}dropDownOptionsSparesSales`,
      payload,
      { signal: abortSignal }
    );
    return Array.isArray(res.data?.result) ? res.data.result : [];
  } catch (error) {
    if (error.name === 'AbortError') {
      // console.log("Request aborted");
      throw error;
    }
    console.error("Error fetching dropDownOptionsSparesSales:", error);
    return [];
  }
};

// API Call 2 - Enhanced with abort signal
export const dropDownTableValuesSparesSales = async (
  parArn,
  childArn,
  filterPayload,
  selectedReg,
  selectedValue,
  abortSignal
) => {
  if (!selectedReg || !selectedValue) return [];

  const values = getArnPayload(parArn, childArn, filterPayload);
  const payload = {
    ...values,
    search_type: selectedReg,
    dropDownValue: selectedValue,
  };

  try {
    const res = await axInstBasic.post(
      `${process.env.REACT_APP_API_URL}dropDownTableValuesSparesSales`,
      payload,
      { signal: abortSignal }
    );
    return Array.isArray(res.data?.result) ? res.data.result : [];
  } catch (error) {
    if (error.name === 'AbortError') {
      // console.log("Request aborted");
      throw error;
    }
    console.error("Error fetching dropDownTableValuesSparesSales:", error);
    return [];
  }
};

const SpareSalesDetailedView = () => {
  const {
    arn: parArn,
    childArn,
    spareSalesPayload,
    isFilterReady,
    isSpOpen,
    setIsSpOpen,
  } = useContext(FilterContext);

  const shouldEnableQuery = Boolean(parArn || childArn);

  const [loading, setLoading] = useState(false);
  const [secondSearch, setSecondSearch] = useState("");

  const [selectedReg, setSelectedReg] = useState("");
  const [selectedValue, setSelectedValue] = useState("");
  const [sparesDetailReportOptionData, setSparesReportOptionData] = useState([]);
  const [sparesDetailsReportsData, setSparessReportsData] = useState([]);
  const [isSecondOpen, setIsSecondOpen] = useState(false);


  // Enhanced refs for better tracking
  const currentRequestRef = useRef(null);
  const abortControllerRef = useRef(null);
  const lastPayloadRef = useRef(null);
  const componentMountedRef = useRef(true);
  const resetInProgressRef = useRef(false);

  // Remove search_type and dropDownValue from payload for clean API calls
  const { search_type, dropDownValue, ...sanitizedPayload } = spareSalesPayload || {};

  // Cancel any ongoing requests
  const cancelCurrentRequests = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    currentRequestRef.current = null;
  }, []);

  // Enhanced reset function
  const resetState = useCallback(() => {
    // console.log("Resetting SpareSalesDetailedView state...");
    resetInProgressRef.current = true;

    cancelCurrentRequests();
    setSelectedReg("");
    setSelectedValue("");
    setSecondSearch("");

    setSparesReportOptionData([]);
    setSparessReportsData([]);
    setLoading(false);

    // Clear the reset flag after a short delay
    setTimeout(() => {
      resetInProgressRef.current = false;
    }, 100);
  }, [cancelCurrentRequests]);

  // Enhanced first dropdown handler
  const handleFirstDropdownChange = async (e) => {
    const selectedFilter = e.target.value;

    // Prevent operations during reset
    if (resetInProgressRef.current) {
      // console.log("Ignoring dropdown change during reset");
      return;
    }

    // Cancel any ongoing requests
    cancelCurrentRequests();

    setSelectedReg(selectedFilter);
    setSelectedValue("");
    setSparessReportsData([]);
    setSparesReportOptionData([]);

    if (!selectedFilter || !shouldEnableQuery || !isFilterReady) {
      return;
    }

    setLoading(true);

    // Create new abort controller
    abortControllerRef.current = new AbortController();
    const requestId = Date.now();
    currentRequestRef.current = requestId;

    try {
      const options = await dropDownOptionsSparesSales(
        parArn,
        childArn,
        sanitizedPayload,
        selectedFilter,
        abortControllerRef.current.signal
      );

      // Check if component is still mounted and request is still current
      if (componentMountedRef.current && currentRequestRef.current === requestId && !resetInProgressRef.current) {
        setSparesReportOptionData(options || []);
      }
    } catch (error) {
      if (error.name !== 'AbortError' && componentMountedRef.current && currentRequestRef.current === requestId && !resetInProgressRef.current) {
        console.error("Error fetching spare options:", error);
        setSparesReportOptionData([]);
      }
    } finally {
      if (componentMountedRef.current && currentRequestRef.current === requestId && !resetInProgressRef.current) {
        setLoading(false);
        currentRequestRef.current = null;
      }
    }
  };

  // Enhanced second dropdown handler
  const handleSecondDropdownChange = async (e) => {
    const selectedOption = e.target.value;

    if (resetInProgressRef.current) {
      return;
    }

    // Cancel any ongoing requests
    cancelCurrentRequests();

    setSelectedValue(selectedOption);
    setSparessReportsData([]);

    if (!selectedOption || !selectedReg || !shouldEnableQuery || !isFilterReady) {
      return;
    }

    setLoading(true);

    // Create new abort controller
    abortControllerRef.current = new AbortController();
    const requestId = Date.now();
    currentRequestRef.current = requestId;

    try {
      const reports = await dropDownTableValuesSparesSales(
        parArn,
        childArn,
        sanitizedPayload,
        selectedReg,
        selectedOption,
        abortControllerRef.current.signal
      );

      // Check if component is still mounted and request is still current
      if (componentMountedRef.current && currentRequestRef.current === requestId && !resetInProgressRef.current) {
        setSparessReportsData(reports || []);
      }
    } catch (error) {
      if (error.name !== 'AbortError' && componentMountedRef.current && currentRequestRef.current === requestId && !resetInProgressRef.current) {
        console.error("Error fetching spare reports:", error);
        setSparessReportsData([]);
      }
    } finally {
      if (componentMountedRef.current && currentRequestRef.current === requestId && !resetInProgressRef.current) {
        setLoading(false);
        currentRequestRef.current = null;
      }
    }
  };

  // Enhanced effect for payload changes
  useEffect(() => {
    if (!isFilterReady) {
      resetState();
      return;
    }

    const currentPayload = JSON.stringify(spareSalesPayload);

    // If this is the first load, just set the reference
    if (lastPayloadRef.current === null) {
      lastPayloadRef.current = currentPayload;
      return;
    }

    // If payload has changed, reset everything
    if (lastPayloadRef.current !== currentPayload) {
      // console.log("Spare sales payload changed, resetting SpareSalesDetailedView");

      // Set reset flag to prevent race conditions
      resetInProgressRef.current = true;

      // Cancel any ongoing requests immediately
      cancelCurrentRequests();

      // Reset state
      setSelectedReg("");
      setSelectedValue("");
      setSparesReportOptionData([]);
      setSparessReportsData([]);
      setLoading(false);

      // Update payload reference
      lastPayloadRef.current = currentPayload;

      // Clear reset flag after state updates
      setTimeout(() => {
        resetInProgressRef.current = false;
      }, 150);
    }
  }, [spareSalesPayload, isFilterReady, resetState, cancelCurrentRequests]);

  const filteredSecondOptions = React.useMemo(() => {
    if (!secondSearch) return sparesDetailReportOptionData;

    return sparesDetailReportOptionData.filter((item) =>
      String(item.key)
        .toLowerCase()
        .includes(secondSearch.toLowerCase())
    );
  }, [secondSearch, sparesDetailReportOptionData]);

  useEffect(() => {
    componentMountedRef.current = true;

    return () => {
      componentMountedRef.current = false;
      cancelCurrentRequests();
    };
  }, [cancelCurrentRequests]);

  // Enhanced conditions for UI states
  const isSecondDropdownDisabled =
    !selectedReg ||
    !Array.isArray(sparesDetailReportOptionData) ||
    sparesDetailReportOptionData.length === 0 ||
    loading ||
    !isFilterReady ||
    resetInProgressRef.current;

  const showTable = sparesDetailsReportsData.length > 0 &&
    !loading &&
    isFilterReady &&
    !resetInProgressRef.current;


  return (
    <div>
      <div className="bg-[#2374BF] lg:w-full sm:w-[390px] md:w-[390px] mt-2  text-md text-white  rounded-t-md font-semibold pl-1 p-1">
        Spare Sales Detailed View
      </div>

      <div className="flex gap-2 mt-2 justify-center justify-items-center">
        {/* First Dropdown */}
        <div className="flex flex-col justify-center">
          <label htmlFor="spareFilterBy" className="text-sm font-medium mb-1">Filter By</label>
          <select
            id="spareFilterBy"
            value={selectedReg}
            onChange={handleFirstDropdownChange}
            disabled={loading || !isFilterReady || resetInProgressRef.current}
            className="border border-gray-300 font-medium w-48 rounded px-3 py-1 disabled:bg-gray-100 disabled:cursor-not-allowed"
          >
            <option value="">
              {!isFilterReady ? "Loading..." : "Select Filter"}
            </option>
            <option value="chassis_number">Chassis Number</option>
            <option value="reg_number">Vehicle Registration Number</option>
            <option value="dealer_name">Dealer Name</option>
            <option value="year_range">Year Range</option>
          </select>
        </div>

        {/* Second Dropdown */}
        {/* Second Dropdown */}
        <div className="flex flex-col relative">
          <label className="text-sm font-medium mb-1">Select Value</label>

          {/* Search box */}
          <input
            type="text"
            value={secondSearch}
            onChange={(e) => {
              setSecondSearch(e.target.value);
              setIsSecondOpen(true);
            }}
            onFocus={() => setIsSecondOpen(true)}
            disabled={isSecondDropdownDisabled}
            placeholder={
              isSecondDropdownDisabled
                ? "Select filter first"
                : "Search value..."
            }
            className="border border-gray-300 w-48 font-medium rounded px-3 py-1 mb-1 disabled:bg-gray-100"
          />

          {/* Dropdown list */}
          {isSecondOpen && (
            <div className="border border-gray-300 rounded max-h-48 overflow-y-auto bg-white absolute top-full left-0 w-full z-10">
              {filteredSecondOptions.length > 0 &&
                filteredSecondOptions.map((item, idx) => (
                  <div
                    key={`${item.key}-${idx}`}
                    onClick={() => {
                      handleSecondDropdownChange({
                        target: { value: item.key },
                      });
                      setSecondSearch(item.key);
                      setIsSecondOpen(false);
                    }}
                    className="px-3 py-2 cursor-pointer hover:bg-blue-100"
                  >
                    {item.key} {item.doc_count ? `(${item.doc_count})` : ""}
                  </div>
                ))}
            </div>
          )}
        </div>
      </div>

      {/* Loading */}
      {(loading || resetInProgressRef.current) && (
        <div className="text-center text-blue-600 mt-4">
          <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
          <span className="ml-2">
            {resetInProgressRef.current ? "Resetting..." : "Loading..."}
          </span>
        </div>
      )}

      {!shouldEnableQuery && (
        <p className="text-center text-red-600 mt-4">Context not ready. Please ensure ARN is set.</p>
      )}

      {!isFilterReady && shouldEnableQuery && (
        <p className="text-center text-gray-500 mt-4">Initializing filters...</p>
      )}

      {isFilterReady && shouldEnableQuery && !selectedReg && !loading && !resetInProgressRef.current && (
        <p className="text-center text-gray-500 mt-4">Please select a filter.</p>
      )}

      {selectedReg && !selectedValue && !loading && isFilterReady && sparesDetailReportOptionData.length > 0 && !resetInProgressRef.current && (
        <p className="text-center text-gray-500 mt-4">Please select a value.</p>
      )}

      {selectedReg && !loading && isFilterReady && sparesDetailReportOptionData.length === 0 && !resetInProgressRef.current && (
        <p className="text-center text-orange-600 mt-4">
          No options available for "{selectedReg}" filter.
        </p>
      )}

      {showTable && (
        <div className="mt-4 w-96 md:w-full">
          <SpareSalesDropDownTable data={sparesDetailsReportsData} />
        </div>
      )}

      {selectedReg && selectedValue && !loading && isFilterReady && sparesDetailsReportsData.length === 0 && !resetInProgressRef.current && (
        <p className="text-center text-gray-500 mt-4">No data available for the selected criteria.</p>
      )}
    </div>
  );
};

export default SpareSalesDetailedView;